import * as React from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import {
  Body,
  Container,
  Head,
  Heading,
  Html,
  Img,
  Preview,
  Section,
  Text,
} from '@react-email/components';

export interface BirthdayEmailProps {
  customerName: string;
  /** Plain-text personalized message written by the sales rep. Newlines are preserved. */
  personalMessage?: string;
  /** Optional sender name shown in the sign-off (e.g. the rep's full name). */
  senderName?: string;
}

export const birthdayEmail = async (
  params: BirthdayEmailProps,
): Promise<{ subject: string; html: string }> => {
  const { customerName, personalMessage, senderName } = params;

  const greetingName = (customerName || '').trim().split(/\s+/)[0] || 'friend';
  const subject = `Happy Birthday from Optic Truck Works, ${greetingName}!`;

  const emailComponent = (
    <Html>
      <Head />
      <Preview>
        Wishing you a fantastic birthday from everyone at Optic Truck Works.
      </Preview>
      <Body
        style={{
          fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
          backgroundColor: '#f5f5f5',
          margin: '0',
          padding: '0',
        }}
      >
        <Container
          style={{
            maxWidth: '600px',
            margin: '0 auto',
            backgroundColor: '#ffffff',
            borderRadius: '8px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            marginTop: '40px',
            marginBottom: '40px',
          }}
        >
          <Section
            style={{
              padding: '32px',
              textAlign: 'center' as const,
              borderBottom: '1px solid #e0e0e0',
            }}
          >
            <Img
              src="https://firebasestorage.googleapis.com/v0/b/otw-production-91ffb.appspot.com/o/public%2FLogoVariation-OpticTruckWorks-05.png?alt=media&token=c3428c05-276c-4d32-ac33-a7d9d51fe380"
              alt="Optic Truck Works Logo"
              style={{
                maxWidth: '200px',
                height: 'auto',
                margin: '0 auto',
              }}
            />
          </Section>

          <Section style={{ padding: '32px' }}>
            <Heading
              style={{
                fontSize: '28px',
                fontWeight: '600',
                color: '#1976d2',
                marginBottom: '24px',
                textAlign: 'center' as const,
              }}
            >
              Happy Birthday, {greetingName}!
            </Heading>

            <Text
              style={{
                fontSize: '16px',
                color: '#333333',
                lineHeight: '1.6',
                marginBottom: '16px',
              }}
            >
              Hi {customerName || 'there'},
            </Text>

            <Text
              style={{
                fontSize: '16px',
                color: '#333333',
                lineHeight: '1.6',
                marginBottom: '24px',
              }}
            >
              Everyone at Optic Truck Works wanted to take a moment to wish you a wonderful birthday.
              We hope your day is filled with great food, great company, and a little time off the road.
            </Text>

            {personalMessage && personalMessage.trim().length > 0 && (
              <Text
                style={{
                  fontSize: '16px',
                  color: '#333333',
                  lineHeight: '1.6',
                  marginBottom: '24px',
                  whiteSpace: 'pre-line' as const,
                }}
              >
                {personalMessage}
              </Text>
            )}

            <Text
              style={{
                fontSize: '16px',
                color: '#333333',
                lineHeight: '1.6',
                marginBottom: '24px',
              }}
            >
              Thanks for being part of the Optic Truck Works family — we’re grateful to have you with us, and
              we’re always here whenever you need us.
            </Text>

            <Text
              style={{
                fontSize: '16px',
                color: '#666666',
                lineHeight: '1.6',
                borderTop: '1px solid #e0e0e0',
                paddingTop: '24px',
                marginTop: '32px',
              }}
            >
              Wishing you the very best,
              <br />
              {senderName ? `${senderName} — ` : ''}The Optic Truck Works Team
            </Text>
          </Section>
        </Container>
      </Body>
    </Html>
  );

  const html = `<!DOCTYPE html>${renderToStaticMarkup(emailComponent)}`;

  return {
    subject,
    html,
  };
};
